import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { FileIcon } from 'lucide-react';

type FileCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const inferNameFromUrl = (url: string): string | undefined => {
  try {
    const u = new URL(String(url), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last) return undefined;
    return decodeURIComponent(last);
  } catch {
    try {
      const s = String(url);
      const last = s.split('?')[0]?.split('#')[0]?.split('/').filter(Boolean).pop();
      return last ? decodeURIComponent(last) : undefined;
    } catch {
      return undefined;
    }
  }
};

const inferSuffixFromName = (name: string): string | undefined => {
  try {
    const base = String(name).trim();
    if (!base) return undefined;
    const lastDot = base.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === base.length - 1) return undefined;
    const ext = base.slice(lastDot + 1).trim();
    if (!ext) return undefined;
    return ext;
  } catch {
    return undefined;
  }
};

export const FileCollapsedSummary = ({ data, className }: FileCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;

  const name =
    (typeof d?.content?.name === 'string' && d.content.name) ||
    (typeof d?.fileName === 'string' && d.fileName) ||
    (typeof d?.text === 'string' && d.text && (typeof d?.fileName === 'string' ? d.fileName : undefined)) ||
    (typeof d?.content?.url === 'string' ? inferNameFromUrl(d.content.url) : undefined) ||
    undefined;

  const suffix = name ? inferSuffixFromName(name) : undefined;

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-inner">
        <FileIcon size={32} className="text-muted-foreground/40" />
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{t('File')}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{name ?? '-'}</div>
        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {/* 中间参数：固定高度容器（占据第 2~4 行），与其它节点的“参数盒子”对齐 */}
          <div className="grid grid-rows-3 gap-1">
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{suffix ?? '-'}</span>
            </div>
            <div className="truncate opacity-0 select-none" aria-hidden="true">
              &nbsp;
            </div>
            <div className="truncate opacity-0 select-none" aria-hidden="true">
              &nbsp;
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


